import { useMemo, useState } from "react";
import { formatAsCrore } from "creso-core/utils";
import { CalculatorInput } from "./calculator-input";
import { AMOUNT_INPUT_MAX } from "./config";
import { CalculatorChart } from "./calculator-chart";
import { CalculatorResultContainer } from "./result-container";
import { Disclaimer } from "./disclaimer";
import { CalculatorInputSummary } from "./input-summary";
import { CalculatorSnapshotContainer } from "./snapshot-container";
import { SnapshotButton } from "./snapshot-btn";

const DEFAULTS = {
  newLumpsumPerMonth: 10_00_000,
  newSIPPerMonth: 25_000,
  expectedReturn: 12,
  commissionRate: 0.7,
  timePeriod: 10,
};

function fvSIP(pmt: number, monthlyRate: number, months: number): number {
  if (months <= 0 || pmt <= 0) return 0;
  if (monthlyRate === 0) return pmt * months;
  return (
    pmt * (((1 + monthlyRate) ** months - 1) / monthlyRate) * (1 + monthlyRate)
  );
}

// Sum of FV of SIPs: Sum(m=1..N) fvSIP(pmt, r, m)
// = pmt*(1+r)^2/r^2 * ((1+r)^N - 1) - pmt*N*(1+r)/r
function totalSIPAUM(
  pmt: number,
  monthlyRate: number,
  totalMonths: number
): number {
  if (totalMonths <= 0 || pmt <= 0) return 0;
  if (monthlyRate === 0) return (pmt * totalMonths * (totalMonths + 1)) / 2;
  const r = monthlyRate;
  const N = totalMonths;
  const powN = (1 + r) ** N;
  const term1 = (pmt * (1 + r) ** 2 * (powN - 1)) / (r * r);
  const term2 = (pmt * N * (1 + r)) / r;
  return term1 - term2;
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function CommissionsCalculator({
  brandRender,
}: {
  brandRender: () => React.ReactNode;
}) {
  const [newLumpsum, setNewLumpsum] = useState(DEFAULTS.newLumpsumPerMonth);
  const [newSIP, setNewSIP] = useState(DEFAULTS.newSIPPerMonth);
  const [expectedReturn, setExpectedReturn] = useState(DEFAULTS.expectedReturn);
  const [commissionRate, setCommissionRate] = useState(DEFAULTS.commissionRate);
  const [timePeriod, setTimePeriod] = useState(DEFAULTS.timePeriod);

  const { lastYearCommission, totalCommission, chartData } = useMemo(() => {
    const monthlyRate = expectedReturn / 100 / 12;
    let cumCommission = 0;
    let lastAUM = 0;
    const points = [];

    for (let year = 1; year <= timePeriod; year += 1) {
      const months = year * 12;
      const aumFromLumpsum = fvSIP(newLumpsum, monthlyRate, months);
      const aumFromSIP = totalSIPAUM(newSIP, monthlyRate, months);
      lastAUM = aumFromLumpsum + aumFromSIP;
      const commissionFromLumpsum = (aumFromLumpsum * commissionRate) / 100;
      const commissionFromSIP = (aumFromSIP * commissionRate) / 100;
      cumCommission += commissionFromLumpsum + commissionFromSIP;
      points.push({
        label: `Y${year}`,
        invested: commissionFromLumpsum,
        gains: commissionFromSIP,
      });
    }

    const last = points[points.length - 1];
    return {
      lastYearCommission: last !== undefined ? last.invested + last.gains : 0,
      totalCommission: cumCommission,
      lastYearTotalAUM: lastAUM,
      chartData: points,
    };
  }, [newLumpsum, newSIP, expectedReturn, commissionRate, timePeriod]);

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-6">
        <CalculatorInput
          label="Monthly Net Lumpsum"
          value={newLumpsum}
          onChange={setNewLumpsum}
          min={0}
          max={5_00_00_000}
          inputMax={AMOUNT_INPUT_MAX}
          step={10_000}
          unit="₹"
          formatDisplay={(v) => formatAsCrore(v, 1, true)}
        />
        <CalculatorInput
          label="Monthly Additional SIP"
          value={newSIP}
          onChange={setNewSIP}
          min={0}
          max={10_00_000}
          inputMax={AMOUNT_INPUT_MAX}
          step={1_000}
          unit="₹"
          formatDisplay={(v) => formatAsCrore(v, 1, true)}
        />
        <CalculatorInput
          label="Expected Rate of Return"
          value={expectedReturn}
          onChange={setExpectedReturn}
          min={1}
          max={50}
          step={0.5}
          unit="%"
        />
        <CalculatorInput
          label="Commission Rate"
          value={commissionRate}
          onChange={setCommissionRate}
          min={0.1}
          max={5}
          step={0.1}
          unit="%"
        />
        <CalculatorInput
          label="Time Period"
          value={timePeriod}
          onChange={setTimePeriod}
          min={1}
          max={40}
          step={1}
          unit="yr"
        />
      </div>

      <CalculatorSnapshotContainer
        brandRender={brandRender}
        inputRender={() => (
          <CalculatorInputSummary
            header="Commisions calculation"
            fields={[
              {
                label: "New Lumpsum Sales every Month",
                value: formatAsCrore(newLumpsum, 1),
              },
              { label: "New SIP every Month", value: formatAsCrore(newSIP, 1) },
              { label: "Expected Rate of Return", value: `${expectedReturn}%` },
              { label: "Commission Rate", value: `${commissionRate}%` },
              { label: "Time Period", value: `${timePeriod} year(s)` },
            ]}
          />
        )}
        resultRender={({ onSnapshot }) => (
          <CalculatorResultContainer>
            <div className="grid grid-cols-2 w-full rounded-xl bg-card px-3 gap-4 py-3">
              <div className="flex flex-col items-center gap-0.5 px-2">
                <span className="text-xs text-muted-foreground text-center">
                  Commission in {ordinal(timePeriod)} Year
                </span>
                <span className="text-base font-semibold text-constructive">
                  {formatAsCrore(lastYearCommission)}
                </span>
              </div>
              <div className="flex flex-col px-2 items-center gap-0.5 border-border">
                <span className="text-xs text-muted-foreground text-center">
                  Commission in {timePeriod} Years
                </span>
                <span className="text-base font-semibold text-constructive">
                  {formatAsCrore(totalCommission)}
                </span>
              </div>
            </div>
            <div className="min-h-85 relative w-full pt-12 mb-2">
              <SnapshotButton onSnapshot={onSnapshot} />
              <CalculatorChart
                data={chartData}
                labels={{
                  invested: "Lumpsum commissions",
                  gains: "SIP commissions",
                }}
              />
            </div>
            <Disclaimer />
          </CalculatorResultContainer>
        )}
      />
    </div>
  );
}
